// src/pages/itsm/PODetail.tsx
import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Sun, Moon, Trash2 } from 'lucide-react'
// @ts-ignore
import { supabase } from '../../supabaseClient'
import { useTheme } from '../../hooks/useTheme'
import { T, Card, Pill, Btn } from '../../components/ui/UI'

type Tone = 'neutral' | 'violet' | 'success' | 'danger' | 'info'

// Resolve a semantic tone into concrete {bg, color, border} css values,
// including the 'neutral' tone (which has no dedicated *Bg/*Border tokens).
function toneColors(tone: Tone): { bg: string; color: string; border: string } {
    if (tone === 'neutral') return { bg: T.surface2, color: T.text2, border: T.border }
    return { bg: (T as any)[`${tone}Bg`], color: (T as any)[tone], border: (T as any)[`${tone}Border`] }
}

export default function PODetail() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { theme, toggleTheme } = useTheme()
    const [searchParams] = useSearchParams()
    const isNew = id === 'new'

    const [po, setPO] = useState<any>(null)
    const [items, setItems] = useState<any[]>([])
    const [vendors, setVendors] = useState<any[]>([])
    const [agents, setAgents] = useState<any[]>([])
    const [loading, setLoading] = useState(!isNew)
    const [saving, setSaving] = useState(false)

    const [form, setForm] = useState({
        title: '',
        description: '',
        vendor_id: '',
        priority: 'medium',
        ticket_id: searchParams.get('ticket_id') || '',
        expected_delivery: '',
        notes: '',
    })

    const [newItem, setNewItem] = useState({ item_name: '', description: '', quantity: 1, unit_price: 0, unit: 'pcs', category: '' })

    useEffect(() => {
        fetchVendors()
        fetchAgents()
        if (!isNew) fetchPO()
    }, [id])

    const fetchPO = async () => {
        const { data } = await supabase
            .from('itsm_purchase_orders')
            .select('*, itsm_vendors(name), itsm_tickets(ticket_number), itsm_po_items(*)')
            .eq('id', id)
            .single()
        setPO(data)
        setItems(data?.itsm_po_items || [])
        setForm({
            title: data?.title || '',
            description: data?.description || '',
            vendor_id: data?.vendor_id || '',
            priority: data?.priority || 'medium',
            ticket_id: data?.ticket_id || '',
            expected_delivery: data?.expected_delivery || '',
            notes: data?.notes || '',
        })
        setLoading(false)
    }

    const fetchVendors = async () => {
        const { data } = await supabase.from('itsm_vendors').select('id, name').eq('is_active', true)
        setVendors(data || [])
    }

    const fetchAgents = async () => {
        const { data } = await supabase.from('itsm_agents').select('id, name').eq('is_active', true)
        setAgents(data || [])
    }

    const savePO = async () => {
        setSaving(true)
        if (isNew) {
            const { data } = await supabase.from('itsm_purchase_orders').insert([{
                ...form,
                vendor_id: form.vendor_id || null,
                ticket_id: form.ticket_id || null,
                status: 'draft',
            }]).select().single()

            if (data && items.length > 0) {
                await supabase.from('itsm_po_items').insert(items.map(i => ({ ...i, po_id: data.id })))
            }
            navigate(`/dashboard/itsm/po/${data.id}`)
        } else {
            await supabase.from('itsm_purchase_orders').update({
                ...form,
                vendor_id: form.vendor_id || null,
                ticket_id: form.ticket_id || null,
            }).eq('id', id)
            fetchPO()
        }
        setSaving(false)
    }

    const addItem = () => {
        if (!newItem.item_name) return
        if (isNew) {
            setItems([...items, { ...newItem, id: Date.now() }])
        } else {
            supabase.from('itsm_po_items').insert([{ ...newItem, po_id: Number(id) }]).then(() => fetchPO())
        }
        setNewItem({ item_name: '', description: '', quantity: 1, unit_price: 0, unit: 'pcs', category: '' })
    }

    const removeItem = async (itemId: number) => {
        if (isNew) {
            setItems(items.filter(i => i.id !== itemId))
        } else {
            await supabase.from('itsm_po_items').delete().eq('id', itemId)
            fetchPO()
        }
    }

    const changeStatus = async (newStatus: string) => {
        await supabase.from('itsm_purchase_orders').update({
            status: newStatus,
            ...(newStatus === 'approved' ? { approved_at: new Date().toISOString() } : {}),
            ...(newStatus === 'ordered' ? { ordered_at: new Date().toISOString() } : {}),
            ...(newStatus === 'received' ? { received_at: new Date().toISOString() } : {}),
        }).eq('id', id)
        fetchPO()
    }

    const totalAmount = items.reduce((s, i) => s + (i.quantity * i.unit_price), 0)

    const STATUS_FLOW: Record<string, string[]> = {
        draft: ['pending_approval'],
        pending_approval: ['approved', 'rejected'],
        approved: ['ordered'],
        ordered: ['received'],
        received: ['closed'],
    }

    const STATUS_CONFIG: Record<string, { label: string; tone: Tone }> = {
        draft: { label: 'Draft', tone: 'neutral' },
        pending_approval: { label: 'Pending Approval', tone: 'violet' },
        approved: { label: 'Approved', tone: 'success' },
        rejected: { label: 'Rejected', tone: 'danger' },
        ordered: { label: 'Ordered', tone: 'info' },
        received: { label: 'Received', tone: 'success' },
        closed: { label: 'Closed', tone: 'neutral' },
    }

    const s = styles

    if (loading) return <div className="jr-app" data-theme={theme} style={s.loading}>Loading...</div>

    const currentStatus = po ? (STATUS_CONFIG[po.status] || STATUS_CONFIG.draft) : null

    return (
        <div className="jr-app" data-theme={theme} style={s.page}>
            <button onClick={() => navigate('/dashboard/itsm/po')} style={s.back}>
                <ArrowLeft size={13} /> Back to POs
            </button>

            <div style={s.header}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        {po && <span className="jr-mono" style={{ fontSize: 13, color: T.text3, fontWeight: 600 }}>{po.po_number}</span>}
                        {po && currentStatus && <Pill tone={currentStatus.tone}>{currentStatus.label}</Pill>}
                    </div>
                    <h1 style={s.title}>{isNew ? 'New Purchase Order' : form.title}</h1>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                        onClick={toggleTheme}
                        aria-label="Toggle dark mode"
                        style={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: `1px solid ${T.borderStrong}`, background: T.surface, color: T.text2, cursor: 'pointer', flexShrink: 0 }}
                    >
                        {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
                    </button>
                    {po && STATUS_FLOW[po.status]?.map(next => {
                        const nextStatus = STATUS_CONFIG[next]
                        const c = toneColors(nextStatus.tone)
                        return (
                            <Btn key={next}
                                style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }}
                                onClick={() => changeStatus(next)}>
                                → {nextStatus.label}
                            </Btn>
                        )
                    })}
                    <Btn variant="primary" style={undefined} onClick={savePO} disabled={saving}>
                        {saving ? 'Saving...' : 'Save PO'}
                    </Btn>
                </div>
            </div>

            <div style={s.grid}>
                <div style={s.main}>
                    {/* PO Form */}
                    <Card>
                        <p style={s.cardLabel}>PO Details</p>
                        <div style={s.field}>
                            <label style={s.label}>Title *</label>
                            <input style={s.input} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Purchase of laptops" />
                        </div>
                        <div style={s.field}>
                            <label style={s.label}>Description</label>
                            <textarea style={{ ...s.input, height: 70, padding: '8px 10px', resize: 'none' as const }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div style={s.field}>
                                <label style={s.label}>Vendor</label>
                                <select style={s.input} value={form.vendor_id} onChange={e => setForm({ ...form, vendor_id: e.target.value })}>
                                    <option value="">Select vendor...</option>
                                    {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                                </select>
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Priority</label>
                                <select style={s.input} value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="urgent">Urgent</option>
                                </select>
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Expected Delivery</label>
                                <input type="date" style={s.input} value={form.expected_delivery} onChange={e => setForm({ ...form, expected_delivery: e.target.value })} />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Related Ticket ID</label>
                                <input style={s.input} value={form.ticket_id} onChange={e => setForm({ ...form, ticket_id: e.target.value })} placeholder="Optional" />
                            </div>
                        </div>
                        <div style={s.field}>
                            <label style={s.label}>Notes</label>
                            <textarea style={{ ...s.input, height: 60, padding: '8px 10px', resize: 'none' as const }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                        </div>
                    </Card>

                    {/* Items */}
                    <Card>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <p style={s.cardLabel}>Items</p>
                            <p className="jr-mono" style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Total: SAR {totalAmount.toLocaleString()}</p>
                        </div>

                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                            <thead>
                                <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                                    {['Item', 'Description', 'Qty', 'Unit', 'Unit Price', 'Total', ''].map(h => (
                                        <th key={h} style={{ fontSize: 10, fontWeight: 700, color: T.text3, textTransform: 'uppercase', padding: '8px 8px', textAlign: 'left' as const }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(item => (
                                    <tr key={item.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                                        <td style={{ padding: '8px', fontSize: 13, fontWeight: 500, color: T.text }}>{item.item_name}</td>
                                        <td style={{ padding: '8px', fontSize: 12, color: T.text2 }}>{item.description}</td>
                                        <td style={{ padding: '8px', fontSize: 13, color: T.text }}>{item.quantity}</td>
                                        <td style={{ padding: '8px', fontSize: 12, color: T.text2 }}>{item.unit}</td>
                                        <td style={{ padding: '8px', fontSize: 13, color: T.text }}>SAR {item.unit_price.toLocaleString()}</td>
                                        <td style={{ padding: '8px', fontSize: 13, fontWeight: 600, color: T.text }}>SAR {(item.quantity * item.unit_price).toLocaleString()}</td>
                                        <td style={{ padding: '8px' }}>
                                            <Btn size="sm" style={{ background: T.dangerBg, color: T.danger, border: 'none' }}
                                                onClick={() => removeItem(item.id)}>
                                                <Trash2 size={11} /> Remove
                                            </Btn>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Add Item */}
                        <div style={{ background: T.surface2, borderRadius: 8, padding: 12 }}>
                            <p style={{ fontSize: 11, fontWeight: 700, color: T.text3, marginBottom: 8, textTransform: 'uppercase' }}>Add Item</p>
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                                <input style={s.inputSm} placeholder="Item name *" value={newItem.item_name} onChange={e => setNewItem({ ...newItem, item_name: e.target.value })} />
                                <input style={s.inputSm} type="number" placeholder="Qty" value={newItem.quantity} onChange={e => setNewItem({ ...newItem, quantity: Number(e.target.value) })} />
                                <input style={s.inputSm} type="number" placeholder="Unit price" value={newItem.unit_price} onChange={e => setNewItem({ ...newItem, unit_price: Number(e.target.value) })} />
                                <input style={s.inputSm} placeholder="Unit (pcs/kg...)" value={newItem.unit} onChange={e => setNewItem({ ...newItem, unit: e.target.value })} />
                                <Btn variant="primary" size="sm" style={undefined} onClick={addItem}>
                                    Add
                                </Btn>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Sidebar */}
                <div style={s.sidebar}>
                    <Card>
                        <p style={s.cardLabel}>Summary</p>
                        <table style={{ width: '100%' }}>
                            <tbody>
                                {[
                                    { label: 'Items', value: items.length },
                                    { label: 'Total', value: `SAR ${totalAmount.toLocaleString()}` },
                                    { label: 'Vendor', value: vendors.find(v => v.id === form.vendor_id)?.name || '—' },
                                    { label: 'Priority', value: form.priority },
                                    { label: 'Delivery', value: form.expected_delivery || '—' },
                                ].map(r => (
                                    <tr key={r.label}>
                                        <td style={{ fontSize: 12, color: T.text3, padding: '5px 0', paddingRight: 12 }}>{r.label}</td>
                                        <td style={{ fontSize: 13, color: T.text, fontWeight: 500, padding: '5px 0', textTransform: 'capitalize' }}>{r.value}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </div>
            </div>
        </div>
    )
}

const styles: Record<string, React.CSSProperties> = {
    page: { padding: '32px 24px', maxWidth: 1200, margin: '0 auto' },
    loading: { textAlign: 'center', padding: '60px', color: T.text3 },
    back: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: T.info, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0, fontWeight: 500 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
    title: { fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 4 },
    grid: { display: 'grid', gridTemplateColumns: '1fr 240px', gap: 20 },
    main: { display: 'flex', flexDirection: 'column', gap: 16 },
    sidebar: { display: 'flex', flexDirection: 'column', gap: 16 },
    cardLabel: { fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 },
    field: { marginBottom: 14 },
    label: { display: 'block', fontSize: 12, fontWeight: 500, color: T.text2, marginBottom: 5 },
    input: { width: '100%', height: 36, border: `1px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 10px', fontSize: 13, color: T.text, outline: 'none', background: T.surface },
    inputSm: { width: '100%', height: 32, border: `1px solid ${T.borderStrong}`, borderRadius: 6, padding: '0 8px', fontSize: 12, color: T.text, outline: 'none', background: T.surface },
}
